import { Outlet } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import * as React from "react";
import { uploadPhotosActions } from "../../store/state-slices/photosUpload";
import "./FileUploadLayout.css";

// Jds
import {
  Container,
  Icon,
  Notifications,
  ProgressBar,
  Spinner,
  Text,
} from "@jds/core";

// Mui
import { Box, IconButton, Typography } from "@mui/material";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Draggable from "react-draggable";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";

// Local
import useApiHttp from "../../hooks/use-http";
import Header from "./Header/Header";
import { createContext, useEffect, useRef, useState } from "react";
import AddButton from "../UI/Buttons/AddButton";

// For generating unique IDs
import { v4 as uuidv4 } from "uuid";
import {
  photoUploadTrigger,
  reindexFaceTrigger,
} from "../../store/state-slices/apiTrigger";
import {
  getDynamicChunkSize,
  getFileMD5,
  getFileNameFromDisposition,
  makeZipImages,
} from "../../util/helper";
import {
  setActiveModalClose,
  setActiveMsg,
  setReindexAlertModalClose,
} from "../../store/state-slices/plans";
import SparkMD5 from "spark-md5";

export const FileUploadContext = createContext(null);

export default function FileUploadLayout(props) {
  const dispatch = useDispatch();

  const { sendRequest: uploadImgRequest } = useApiHttp();
  const { sendRequest: chunkInitiateRequest } = useApiHttp();
  const { sendRequest: chunkUploadRequest } = useApiHttp();
  const { sendRequest: downloadRequest } = useApiHttp();
  const { sendRequest: reindexRequest, isLoading: isReindexLoading } =
    useApiHttp();
  const { sendRequest: reindexStatusReq, isLoading: isReindexStatusLoading } =
    useApiHttp();
  const [reindexStaus, setReindexStatus] = useState({});
  const [reindexProgress, setReindexProgress] = useState({});
  const [folderNameActive, setFolderNameActive] = useState(false);
  const [tasks, setTasks] = useState([]); // State to hold task info
  const queue = useRef([]); // Queue to manage calls
  const isProcessing = useRef(false); // Flag to check if processing

  const [downloadTasks, setDownloadTasks] = useState([]);
  const downloadQueue = useRef([]);

  const handleToggleActivity = (key) => {
    setFolderNameActive(key);
  };

  //For dialog
  function PaperComponent(props) {
    return (
      <Draggable
        handle="#draggable-dialog-title"
        cancel={'[class*="MuiDialogContent-root"]'}>
        <Paper {...props} />
      </Draggable>
    );
  }
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = (key) => {
    setOpen(true);
    setFolderNameActive(key);
  };

  const handleClose = () => {
    setOpen(false);
  };
  //dialog ends here

  const handleFileUpload = (files, eventId, folderId, folderName) => {
    // console.log(files.length);
    dispatch(uploadPhotosActions.setTotalCount({ totalCount: files.length }));
    dispatch(photoUploadTrigger({ id: eventId + folderId, value: false }));
    if (
      queue.current.filter(
        (val) => val.eventId === eventId && val.folderId === folderId,
      ).length > 0
    ) {
      queue.current = queue.current.map((val) => {
        if (val.eventId === eventId && val.folderId) {
          return {
            ...val,
            files: [...val.files, ...files],
          };
        } else {
          return val;
        }
      });
      setTasks((prevTasks) =>
        prevTasks.map((task) => {
          if (task.eventId === eventId && task.folderId === folderId) {
            return {
              ...task,
              totalFiles: task.totalFiles + files.length,
            };
          } else {
            return task;
          }
        }),
      ); // Add task to state
    } else {
      const taskId = uuidv4(); // Generate a unique ID for the task
      const controller = new AbortController(); // Create an AbortController for the task
      queue.current.push({ files, eventId, folderId, taskId, controller });
      setTasks((prevTasks) => [
        ...prevTasks,
        {
          files,
          eventId,
          folderId,
          folderName,
          uploadComplete: 0,
          uploadPercentage: 0,
          totalFiles: files.length,
          taskId,
          controller,
        },
      ]); // Add task to state
    }
    processQueue();
  };

  const singleFileUpload = (formData, signal) => {
    const controller = signal || new AbortController();
    const { signal: fetchSignal } = controller;

    const singleFileUploadPromise = new Promise((res, rej) => {
      uploadImgRequest(
        {
          url: `uploadMedia`,
          method: "POST",
          body: formData,
          formData: true,
          base: "MEDIA",
          signal: fetchSignal,
        },
        null,
        (data) => {
          res();
        },
        (err, data) => {
          // if (data?.statusCode !== 403 && data?.statusCode !== 402) {
          //   const notificationId = uuidv4();
          //   Notifications.toast.error({
          //     // id: "upload-photos-error",
          //     id: notificationId,
          //     title: data?.message,
          //   });
          // }
          rej(data);
        },
      );
    });
    if (fetchSignal) {
      fetchSignal.addEventListener("abort", () => {
        controller.abort();
      });
    }
    return singleFileUploadPromise;
  };

  const uploadInChunks = async (file, eventId, folderId, signal) => {
    const hash = await getFileMD5(file);

    const chunkInitiatePayload = {
      name: file.name,
      size: file.size,
      hash,
      eventId,
      folderId,
    };

    const initData = await new Promise((resolve, reject) => {
      chunkInitiateRequest(
        {
          url: "initiate/chunk/upload/organizer",
          base: "MEDIA",
          method: "POST",
          body: chunkInitiatePayload,
          signal,
        },
        () => {}, // optional onStart
        (data, res) => {
          if (res.status === 201) {
            // File already uploaded, skip
            resolve(null);
          } else {
            resolve(data);
          }
        },
        (err, data) => {
          reject(data || err);
        },
      );
    });

    // If already uploaded
    if (!initData) return;

    const { transactionId: uploadId, offset = 0 } = initData.data;
    const chunkSize = getDynamicChunkSize(file.size);
    let currentOffset = offset;

    while (currentOffset < file.size) {
      const chunk = file.slice(currentOffset, currentOffset + chunkSize);
      const chunkBuffer = await chunk.arrayBuffer();
      const chunkHash = SparkMD5.ArrayBuffer.hash(chunkBuffer);

      const formData = new FormData();
      formData.append("chunkFile", new Blob([chunkBuffer]), file.name);
      formData.append("uploadId", uploadId);
      formData.append("chunkLength", chunk.size);
      formData.append("chunkHash", chunkHash);
      formData.append("offset", currentOffset);
      formData.append("eventId", eventId);
      formData.append("fileName", file.name);
      formData.append("folderId", folderId);

      await new Promise((resolve, reject) => {
        chunkUploadRequest(
          {
            url: "chunk/upload/organizer",
            base: "MEDIA",
            method: "POST",
            formData: true,
            body: formData,
            signal,
          },
          () => {},
          () => resolve(),
          (err) => reject(err),
        );
      });

      currentOffset += chunkSize;
    }
  };

  // Function to process the queue
  // const processQueue = async () => {
  //   let currentTask = queue.current.shift();
  //   try {
  //     for (let i = 0; i < currentTask.files.length; i++) {
  //       const formData = new FormData();
  //       formData.append("uploadFile", currentTask.files[i]);
  //       formData.append("eventId", currentTask.eventId);
  //       formData.append("folderId", currentTask.folderId);

  //       const value = await singleFileUpload(formData, currentTask.controller)
  //         .then()
  //         .catch((data) => {
  //           if (data?.statusCode === 403 || data?.statusCode === 402) {
  //             dispatch(setActiveModalClose(false));
  //             dispatch(setActiveMsg(data?.errors?.planType));
  //             setTasks([]);
  //             currentTask = [];
  //             queue.current = [];
  //           }
  //         })
  //         .finally(() => {
  //           setTasks((prevTasks) =>
  //             prevTasks.map((task) => {
  //               if (currentTask.taskId === task.taskId) {
  //                 return {
  //                   ...task,
  //                   uploadComplete: task.uploadComplete + 1,
  //                   uploadPercentage:
  //                     Math.floor(
  //                       ((task.uploadComplete + 1) / task.totalFiles) * 100
  //                     ) || 0,
  //                 };
  //               } else {
  //                 return task;
  //               }
  //             })
  //           ); // Add task to state
  //         });
  //     }
  //   } catch (err) {
  //   } finally {
  //     dispatch(
  //       photoUploadTrigger({
  //         id: currentTask.eventId + currentTask.folderId,
  //         value: true,
  //       })
  //     );
  //     dispatch(uploadPhotosActions.setTotalCount({ totalCount: 0 }));
  //     queue.current = queue.current.filter(
  //       (task) => task.taskId !== currentTask.taskId
  //     ); // Remove task from queue
  //     setTasks((prevTasks) =>
  //       prevTasks.filter((task) => task.taskId !== currentTask.taskId)
  //     ); // Remove task from state
  //   }
  // };
  const processQueue = async () => {
    let currentTask = queue.current.shift();
    try {
      for (let i = 0; i < currentTask.files.length; i++) {
        const file = currentTask.files[i];
        const formData = new FormData();
        formData.append("uploadFile", file);
        formData.append("eventId", currentTask.eventId);
        formData.append("folderId", currentTask.folderId);

        const isChunkUpload = file.size > 10 * 1024 * 1024;

        const uploadFn = isChunkUpload
          ? () =>
              uploadInChunks(
                file,
                currentTask.eventId,
                currentTask.folderId,
                currentTask.controller.signal,
              )
          : () => singleFileUpload(formData, currentTask.controller);

        await uploadFn()
          .catch((data) => {
            if (data?.statusCode === 403 || data?.statusCode === 402) {
              dispatch(setActiveModalClose(false));
              dispatch(setActiveMsg(data?.errors?.planType));
              setTasks([]);
              currentTask = [];
              queue.current = [];
            } else {
              const notificationId = uuidv4();
              Notifications.toast.error({
                // id: "upload-photos-error",
                id: notificationId,
                title: data?.message,
              });
            }
          })
          .finally(() => {
            setTasks((prevTasks) =>
              prevTasks.map((task) => {
                if (currentTask.taskId === task.taskId) {
                  const uploaded = task.uploadComplete + 1;
                  return {
                    ...task,
                    uploadComplete: uploaded,
                    uploadPercentage: Math.floor(
                      (uploaded / task.totalFiles) * 100,
                    ),
                  };
                } else return task;
              }),
            );
          });
      }
    } catch (err) {
      console.error("Queue error:", err);
    } finally {
      dispatch(
        photoUploadTrigger({
          id: currentTask.eventId + currentTask.folderId,
          value: true,
        }),
      );
      dispatch(uploadPhotosActions.setTotalCount({ totalCount: 0 }));
      queue.current = queue.current.filter(
        (task) => task.taskId !== currentTask.taskId,
      );
      setTasks((prevTasks) =>
        prevTasks.filter((task) => task.taskId !== currentTask.taskId),
      );
    }
  };

  // Function to cancel a specific task
  const cancelTask = (taskId, eventId, folderId) => {
    const taskToCancel = tasks.find((task) => task.taskId === taskId);
    if (taskToCancel) {
      taskToCancel.controller.abort(); // Abort the task
      queue.current = queue.current.filter((task) => task.taskId !== taskId); // Remove task from queue
      setTasks((prevTasks) =>
        prevTasks.filter((task) => task.taskId !== taskId),
      ); // Remove task from state
      dispatch(
        photoUploadTrigger({
          id: eventId + folderId,
          value: true,
        }),
      );
      dispatch(uploadPhotosActions.setTotalCount({ totalCount: 0 }));
    }
  };

  const handleFileDownloadOP = (files, eventId, role = "ADMIN") => {
    const taskId = uuidv4(); // Generate a unique ID for the task
    const controller = new AbortController(); // Create an AbortController for the task
    downloadQueue.current.push({ files, taskId, controller, eventId, role });
    setDownloadTasks((prevTasks) => [
      ...prevTasks,
      {
        uploadComplete: 0,
        uploadPercentage: 0,
        totalFiles: files.length,
        taskId,
        controller,
      },
    ]);
    processDownloadQueueOp();
  };

  const processDownloadQueueOp = async () => {
    const currentTask = downloadQueue.current.shift();
    let downloadedImageBlobs = [];
    try {
      for (let i = 0; i < currentTask.files.length; i++) {
        // const formData = new FormData();
        // formData.append("mediaId", currentTask?.files[i]);
        // formData.append("eventId", currentTask?.eventId);
        let bodyData = {
          mediaId: currentTask?.files[i],
          eventId: currentTask?.eventId,
        };
        const value = await singleDownloadImg(
          bodyData,
          currentTask.controller,
          currentTask?.role,
        )
          .then(({ data: downloadBlob, res }) => {
            downloadedImageBlobs.push({
              blob: downloadBlob,
              filename: getFileNameFromDisposition(res),
            });
          })
          .catch((err) => {})
          .finally(() => {
            setDownloadTasks((prevTasks) =>
              prevTasks.map((task) => {
                if (currentTask.taskId === task.taskId) {
                  return {
                    ...task,
                    uploadComplete: task.uploadComplete + 1,
                    uploadPercentage:
                      Math.floor(
                        ((task.uploadComplete + 1) / task.totalFiles) * 100,
                      ) || 0,
                  };
                } else {
                  return task;
                }
              }),
            ); // Add task to state
          });
      }
    } catch (err) {
    } finally {
      makeZipImages(downloadedImageBlobs, "event-images");
      setDownloadTasks((prevTasks) =>
        prevTasks.filter((task) => task.taskId !== currentTask.taskId),
      ); // Remove task from state
    }
  };

  const handleFileDownload = (files) => {
    const taskId = uuidv4(); // Generate a unique ID for the task
    const controller = new AbortController(); // Create an AbortController for the task
    downloadQueue.current.push({ files, taskId, controller });
    setDownloadTasks((prevTasks) => [
      ...prevTasks,
      {
        uploadComplete: 0,
        uploadPercentage: 0,
        totalFiles: files.length,
        taskId,
        controller,
      },
    ]);
    processDownloadQueue();
  };

  const processDownloadQueue = async () => {
    const currentTask = downloadQueue.current.shift();
    let downloadedImageBlobs = [];
    try {
      for (let i = 0; i < currentTask.files.length; i++) {
        // const formData = new FormData();
        // formData.append("mediaId", currentTask?.files[i]?.id);
        // formData.append("eventId", currentTask?.files[i]?.eventId);
        let bodyData = {
          mediaId: currentTask?.files[i]?.id,
          eventId: currentTask?.files[i]?.eventId,
        };
        const value = await singleDownloadImg(bodyData, currentTask.controller)
          .then((downloadBlob) => {
            downloadedImageBlobs.push(downloadBlob);
          })
          .catch((err) => {})
          .finally(() => {
            setDownloadTasks((prevTasks) =>
              prevTasks.map((task) => {
                if (currentTask.taskId === task.taskId) {
                  return {
                    ...task,
                    uploadComplete: task.uploadComplete + 1,
                    uploadPercentage:
                      Math.floor(
                        ((task.uploadComplete + 1) / task.totalFiles) * 100,
                      ) || 0,
                  };
                } else {
                  return task;
                }
              }),
            ); // Add task to state
          });
      }
    } catch (err) {
    } finally {
      makeZipImages(downloadedImageBlobs, "event-images");
      setDownloadTasks((prevTasks) =>
        prevTasks.filter((task) => task.taskId !== currentTask.taskId),
      ); // Remove task from state
    }
  };

  const handleReindexStatusReq = ({ eventId, requestId }) => {
    reindexStatusReq(
      {
        url: `face/cluster/status?eventId=${eventId}&requestId=${requestId}`,
        method: "GET",
        base: "MEDIA",
      },
      null,
      (data) => {
        if (
          data?.data?.clusteringStatus === "COMPLETED" &&
          data?.data?.croppingStatus === "COMPLETED"
        ) {
          setReindexStatus((pre) => {
            return { ...pre, [eventId]: "COMPLETED" };
          });
          dispatch(reindexFaceTrigger({ id: eventId, value: true }));
          dispatch(setReindexAlertModalClose(true));
        }
        setReindexProgress((pre) => {
          return { ...pre, [eventId]: data?.data };
        });
        if (
          data?.data?.clusteringStatus !== "COMPLETED" ||
          data?.data?.croppingStatus !== "COMPLETED"
        ) {
          setTimeout(() => {
            handleReindexStatusReq({ eventId, requestId });
          }, 5000);
        }
      },
      (err, data) => {
        Notifications.toast.error({
          id: "reindex-process-error",
          title: data?.message,
        });
        setReindexProgress((pre) => {
          return { ...pre, [eventId]: null };
        });
        setReindexStatus((pre) => {
          return { ...pre, [eventId]: "ERROR" };
        });
        dispatch(reindexFaceTrigger({ id: eventId, value: true }));
        dispatch(setReindexAlertModalClose(true));
      },
    );
  };

  const handleReindex = ({ eventId }) => {
    setReindexStatus((pre) => {
      return { ...pre, [eventId]: "PENDING" };
    });
    dispatch(reindexFaceTrigger({ id: eventId, value: false }));

    reindexRequest(
      {
        url: `face/cluster/reindex?eventId=${eventId}`,
        method: "GET",
        base: "MEDIA",
      },
      null,
      (data) => {
        handleReindexStatusReq({ eventId, requestId: data?.data?.requestId });
      },
      (err, data) => {
        setReindexStatus((pre) => {
          return { ...pre, [eventId]: "ERROR" };
        });
        Notifications.toast.error({
          // id: "upload-photos-error",
          id: "reindexing-error",
          title: data?.message,
        });
      },
    );
  };

  const singleDownloadImg = (formData, signal, role) => {
    const controller = signal || new AbortController();
    const { signal: fetchSignal } = controller;

    const singleFileDownloadPromise = new Promise((resolve, reject) => {
      downloadRequest(
        {
          url: role == "ADMIN" ? `organizer/download` : `photographer/download`,
          method: "POST",
          base: "MEDIA",
          // formData: true,
          body: formData,
          signal: fetchSignal,
        },
        null,
        (data, res) => {
          const imageUrl = URL.createObjectURL(data);
          resolve({ data, res }); // Resolve the Promise with the imageUrl
        },
        (err, data) => {
          Notifications.toast.error({
            id: "download-photos-error",
            title: data?.message,
          });
          reject(err);
        },
      );
    });

    if (fetchSignal) {
      fetchSignal.addEventListener("abort", () => {
        controller.abort();
      });
    }
    return singleFileDownloadPromise;
  };

  const cancelDownloadTask = (taskId) => {
    const taskToCancel = downloadTasks.find((task) => task.taskId === taskId);
    if (taskToCancel) {
      taskToCancel.controller.abort(); // Abort the task
      downloadQueue.current = downloadQueue.current.filter(
        (task) => task.taskId !== taskId,
      ); // Remove task from queue
      setDownloadTasks((prevTasks) =>
        prevTasks.filter((task) => task.taskId !== taskId),
      ); // Remove task from state
    }
  };
  const alertUser = (e) => {
    if (
      tasks.length > 0 ||
      downloadTasks.length > 0 ||
      (typeof reindexStaus === "object" && reindexStaus !== null
        ? Object.values(reindexStaus).includes("PENDING")
        : reindexStaus === "PENDING")
    ) {
      e.preventDefault();
      const message = "Are you sure you want to leave this page?";
      e.returnValue = message;
      return message;
    }
  };

  useEffect(() => {
    window.addEventListener("beforeunload", alertUser);
    return () => {
      window.removeEventListener("beforeunload", alertUser);
    };
  }, [tasks, downloadTasks, reindexStaus]);

  return (
    <FileUploadContext.Provider
      value={{
        handleFileUpload,
        setTasks,
        handleFileDownload,
        setDownloadTasks,
        handleFileDownloadOP,
        handleReindex,
        reindexStaus,
        reindexProgress,
      }}>
      <Outlet />

      <Box
        sx={{
          position: "fixed",
          right: "24px",
          bottom: "24px",
          zIndex: 100,
        }}>
        <div
          className="upload-toast-container"
          style={{
            backgroundColor: `${
              tasks.length !== 0 ? "rgba(0, 0, 0, .65)" : ""
            }`,
          }}>
          {tasks.length !== 0 ? (
            <div className="upload-toast-heading">
              <Spinner size="small" kind="inline" />
              <Text text="Uploading in progress" color="white" />
            </div>
          ) : (
            ""
          )}
          {tasks.map((task) => (
            <div>
              <div className="upload-toast">
                <div className="toast-minimized">
                  {task.uploadPercentage !== 100 && (
                    <Spinner size="small" kind="inline" />
                  )}
                  <Text
                    color="white"
                    text={`Uploading to ${task?.folderName}`}
                  />
                </div>
                <div className="icons-buttons">
                  <IconButton
                    onClick={() => {
                      handleClickOpen(task.taskId);
                    }}>
                    <ArrowDropUpIcon fontSize="large" sx={{ color: "white" }} />
                  </IconButton>
                  <Icon
                    ic="IcErrorColored"
                    onClick={() => {
                      cancelTask(task.taskId, task.eventId, task.folderId);
                    }}
                  />
                </div>
              </div>
              {/* <ProgressBar
                state={task.uploadPercentage === 100 ? "success" : "warning"}
                value={
                  task.uploadComplete == task.totalFiles
                    ? 100
                    : task.uploadComplete / task.totalFiles
                }
                label={
                  <Container>
                    {task.uploadPercentage !== 100 && (
                      <Spinner size="small" kind="inline" />
                    )}
                    <Text
                      color="white"
                      text={`Uploaded ${task.uploadComplete} of ${task.totalFiles}`}
                    />
                  </Container>
                }
                valueLabel={
                  <Text color="white" text={task.uploadPercentage + "%"} />
                }
              /> */}
              {/* </Box> */}
              {/* Expanded Upload Dialog  */}
              {task.taskId === folderNameActive ? (
                <Dialog
                  open={open}
                  onClose={handleClose}
                  PaperComponent={PaperComponent}
                  aria-labelledby="draggable-dialog-title"
                  sx={{ minwidth: "800px", fontSize: "0.5rem" }}>
                  <DialogTitle
                    style={{
                      cursor: "move",
                      background:
                        "linear-gradient(to right, #3535f3ff, #f0f0f0)",
                    }}
                    id="draggable-dialog-title">
                    <div className="dialog-title">
                      <Container gap={8}>
                        {task.uploadPercentage !== 100 && (
                          <Spinner size="small" kind="inline" />
                        )}
                        <Text
                          color="white"
                          text={`Uploading ${task.totalFiles} items`}
                        />
                      </Container>
                      <IconButton onClick={handleClose}>
                        <CloseFullscreenIcon sx={{ color: "#3535f3ff" }} />
                      </IconButton>
                    </div>
                  </DialogTitle>
                  <DialogContent sx={{ padding: "20px 0px" }}>
                    {/* <div className="dialog-content">
                      <div
                        style={{
                          paddingLeft: "30px",
                          display: "flex",
                          gap: "10px",
                        }}
                      >
                        {task.uploadPercentage !== 100 && (
                          <Spinner size="small" kind="inline" />
                        )}
                        <Text
                          color="white"
                          text={`Uploading ${task.totalFiles} items`}
                        />
                      </div>
                    </div> */}
                    <DialogContentText
                      style={{ paddingTop: "24px", width: "80%" }}>
                      <div className="dialog-content-text">
                        <div style={{ width: "500px" }}>
                          <Text
                            color="black"
                            text={`Name: ${
                              task?.files[task.uploadComplete]?.name
                            }`}
                          />
                        </div>
                        <Text color="black" text={`To: ${task?.folderName}`} />
                        <Text
                          color="black"
                          text={`Items remaining: ${
                            task.totalFiles - task.uploadComplete
                          }`}
                        />

                        <div style={{ width: "480px" }}>
                          <ProgressBar
                            state={
                              task.uploadPercentage === 100
                                ? "success"
                                : "warning"
                            }
                            value={
                              task.uploadComplete == task.totalFiles
                                ? 100
                                : task.uploadComplete / task.totalFiles
                            }
                            valueLabel={
                              <Text
                                color="black"
                                text={task.uploadPercentage + "%"}
                              />
                            }
                          />
                        </div>
                      </div>
                    </DialogContentText>
                  </DialogContent>
                </Dialog>
              ) : (
                ""
              )}
              {/* Dialog Ends Here  */}
            </div>
          ))}
        </div>
        {downloadTasks.map((task) => (
          <Box
            width={300}
            sx={{
              padding: "24px ",
              margin: "24px ",
              borderRadius: "16px",
              backgroundColor: "rgba(0, 0, 0, .65)",
              position: "relative",
            }}
            key={task.taskId}>
            <div style={{ position: "absolute", top: "12px", right: "12px" }}>
              <Icon
                ic="IcErrorColored"
                onClick={() => {
                  cancelDownloadTask(task.taskId);
                }}
              />
            </div>
            <Box mt={2.5}>
              <ProgressBar
                state={task.uploadPercentage === 100 ? "success" : "warning"}
                value={
                  task.uploadComplete == task.totalFiles
                    ? 100
                    : task.uploadComplete / task.totalFiles
                }
                label={
                  <Container>
                    {task.uploadPercentage !== 100 && (
                      <Spinner size="small" kind="inline" />
                    )}
                    <Text
                      color="white"
                      text={`${"Download"} ${task.uploadComplete} of ${
                        task.totalFiles
                      }`}
                    />
                  </Container>
                }
                valueLabel={
                  <Text color="white" text={task.uploadPercentage + "%"} />
                }
                style={{
                  marginTop: "8px",
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </FileUploadContext.Provider>
  );
}
